import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import ModalBox from "../../themes/overrides/ModalBox";
import DeleteIcon from "../../assets/images/icons/delete_icon.svg";
import EditIcon from "../icons/EditIcon";
import AddIcon from "@mui/icons-material/Add";
import CreateWebhooks from "./CreateWebhooks";
import { getWebHooks, removeWebHooks } from "../../actions/webHookAction";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks/use-dispatch";
import { toast } from "sonner";
import DeleteConfirmationModal from "../DeleteConfirmationModal";

const ManageWebHooks = ({ open, onClose, selectedType, onSuccess }) => {
  const [createWebhooksPopup, setCreateWebhooksPopup] = useState(false);
  const { workspaceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedWebHook, setSelectedWebHook] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedContentToDelete, setSelectedContentToDelete] = useState(null);
  const webhooks = useSelector((state) => state.webhooks.webhooks);

  const dispatch = useAppDispatch();

  // Fetch webhooks data
  const getWebHooksData = useCallback(async () => {
    if (workspaceId) {
      setLoading(true);
      try {
        const state = await dispatch(getWebHooks(workspaceId));
        if (!state?.success) {
          toast.error(state?.message || "Something went wrong");
        }
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [workspaceId]);

  // Handle create webhook button click
  const handleCreateWebhook = () => {
    setCreateWebhooksPopup(true);
    setEditMode(false);
    setSelectedWebHook(null);
  };

  // Handle remove webhook button click
  const handleConfirmDelete = async () => {
    const alert = toast.loading("Event is being processed...");
    try {
      const state = await dispatch(removeWebHooks(selectedContentToDelete));
      // handle response state
      if (state?.success) {
        onSuccess?.();
        toast.success(state?.message || "WebHook removed successfully", {
          id: alert,
        });
        getWebHooksData();
        setDeleteModalOpen(false);
        setSelectedContentToDelete(null);
      } else {
        toast.error(state?.message || "Something went wrong", {
          id: alert,
        });
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong", {
        id: alert,
      });
    }
  };

  // Handle edit webhook button click
  const handleEditWebHook = (id) => {
    const webHookToEdit = webhooks.find((webhook) => webhook._id === id);

    if (webHookToEdit) {
      setSelectedWebHook(webHookToEdit);
      setEditMode(true);
      setCreateWebhooksPopup(true);
    }
  };

  // handle count triggers functions count
  const handleCountTriggers = (triggers) => {
    let count = 0;
    for (let i = 0; i < triggers.length; i++) {
      count += triggers[i].functions.length;
    }
    return count;
  };

  // Trigger fetching webhooks data on component mount
  useEffect(() => {
    getWebHooksData();
  }, [getWebHooksData]);

  return (
    <>
      {/* Modal for managing webhooks */}
      <Modal open={open} onClose={onClose}>
        <ModalBox sx={{ width: "900px", maxWidth: "95%" }}>
          {loading && (
            <div className="flex items-center justify-center h-full">
              <Typography variant="h6">Loading...</Typography>
            </div>
          )}

          {!loading && (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <Typography variant="h2">Manage WebHooks</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateWebhook}
                >
                  Create Webhook
                </Button>
              </div>

              {/* Table */}
              <TableContainer className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHead className="bg-gray-100">
                    <TableRow>
                      <TableCell className="font-bold">Name</TableCell>
                      <TableCell className="font-bold">Request Type</TableCell>
                      <TableCell className="font-bold">Status</TableCell>
                      <TableCell className="font-bold">URL</TableCell>
                      <TableCell className="font-bold">Trigger</TableCell>
                      <TableCell className="font-bold text-right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhooks?.map((webhook) => (
                      <TableRow key={webhook._id} hover>
                        <TableCell>{webhook.name}</TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${webhook.requestType === "GET"
                                ? "bg-green-100 text-green-800"
                                : webhook.requestType === "POST"
                                  ? "bg-blue-100 text-blue-800"
                                  : webhook.requestType === "PUT"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : webhook.requestType === "DELETE"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {webhook.requestType}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${webhook.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"

                              }`}
                          >
                            {webhook.active ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell className="text-blue-600">
                          {webhook.apiEndpoint}
                        </TableCell>
                        <TableCell>
                          {handleCountTriggers(webhook?.triggers)} Triggers
                        </TableCell>
                        <TableCell className="">
                          <div className="flex space-x-1">
                            <IconButton
                              color="secondary"
                              aria-label="edit"
                              className="text-gray-600 hover:text-blue-600"
                              onClick={() => handleEditWebHook(webhook._id)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              sx={{
                                color: "#FF4842",
                              }}
                              onClick={() => {
                                setSelectedContentToDelete(webhook._id);
                                setDeleteModalOpen(true);
                              }}
                            >
                              <img src={DeleteIcon} alt="delete" />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Empty state */}
              {webhooks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <Typography variant="body1">No webhooks found</Typography>
                </div>
              )}
            </div>
          )}
        </ModalBox>
      </Modal>

      {/* Create/Edit Webhook Popup */}
      <CreateWebhooks
        open={createWebhooksPopup}
        onClose={() => {
          setCreateWebhooksPopup(false);
          setEditMode(false);
          setSelectedWebHook(null);
        }}
        editMode={editMode}
        webhook={selectedWebHook}
        onSuccess={() => getWebHooksData()}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedContentToDelete(null);
        }}
        text="Are you sure you want to delete this content?"
        onConfirm={handleConfirmDelete}
        deleteButtonLabel="Delete"
      />
    </>
  );
};

export default ManageWebHooks;
